import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const Stocktake = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stocktake, setStocktake] = useState(null);
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaItems, setAreaItems] = useState([]);
  const [entries, setEntries] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState({}); // Track save status per item
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // Refs for debouncing
  const saveTimers = useRef({});
  const pendingQueue = useRef([]);

  // Fetch stocktake data on mount
  useEffect(() => {
    const init = async () => {
      await fetchStocktakeData();
      loadCachedEntries();
    };
    
    init();
    
    // Setup online/offline detection
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingEntries();
    };
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      // Clear all pending timers
      const timers = saveTimers.current;
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Only run on mount and when id changes

  useEffect(() => {
    if (selectedArea) {
      fetchAreaItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea]); // fetchAreaItems is memoized and only depends on selectedArea

  // Helper functions for caching
  const saveCachedEntries = useCallback((key, entry) => {
    try {
      const cached = localStorage.getItem(`stocktake_${id}_cache`) || '{}';
      const cachedEntries = JSON.parse(cached);
      cachedEntries[key] = entry;
      localStorage.setItem(`stocktake_${id}_cache`, JSON.stringify(cachedEntries));
    } catch (err) {
      console.error('Failed to cache entry:', err);
    }
  }, [id]);

  const clearCachedEntry = useCallback((key) => {
    try {
      const cached = localStorage.getItem(`stocktake_${id}_cache`) || '{}';
      const cachedEntries = JSON.parse(cached);
      delete cachedEntries[key];
      localStorage.setItem(`stocktake_${id}_cache`, JSON.stringify(cachedEntries));
    } catch (err) {
      console.error('Failed to clear cached entry:', err);
    }
  }, [id]);

  const loadCachedEntries = useCallback(() => {
    try {
      const cached = localStorage.getItem(`stocktake_${id}_cache`);
      if (cached) {
        const cachedEntries = JSON.parse(cached);
        setEntries(prev => ({ ...prev, ...cachedEntries }));
      }
    } catch (err) {
      console.error('Failed to load cached entries:', err);
    }
  }, [id]);

  // Save entry to server
  const saveEntryToServer = useCallback(async (key, item, entry) => {
    const data = {
      stockItemId: item.id,
      stockAreaId: selectedArea,
      cartons: parseFloat(entry.cartons || 0),
      sleeves: parseFloat(entry.sleeves || 0),
      units: parseFloat(entry.units || 0),
      notes: entry.notes || ''
    };

    if (entry.id) {
      // Update existing entry
      await api.put(`/stocktakes/entries/${entry.id}`, data);
    } else {
      // Create new entry
      const response = await api.post(`/stocktakes/${id}/entries`, data);
      setEntries(prev => ({
        ...prev,
        [key]: { ...prev[key], id: response.data.entryId }
      }));
    }
  }, [id, selectedArea]);

  const fetchStocktakeData = useCallback(async () => {
    try {
      const [stocktakeRes, areasRes, entriesRes] = await Promise.all([
        api.get(`/stocktakes/${id}`),
        api.get('/stock-areas'),
        api.get(`/stocktakes/${id}/entries`)
      ]);
      
      setStocktake(stocktakeRes.data);
      setAreas(areasRes.data.areas);
      
      // Convert entries to map for easy lookup
      const entriesMap = {};
      entriesRes.data.entries.forEach(entry => {
        const key = `${entry.stock_area_id}-${entry.stock_item_id}`;
        entriesMap[key] = {
          id: entry.id,
          cartons: entry.cartons,
          sleeves: entry.sleeves,
          units: entry.units,
          notes: entry.notes
        };
      });
      setEntries(entriesMap);
      
      loadCachedEntries(); // Merge with any cached entries
    } catch (err) {
      console.error('Failed to load stocktake:', err);
      alert('Failed to load stocktake');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, loadCachedEntries]);

  const syncPendingEntries = useCallback(async () => {
    const queue = [...pendingQueue.current];
    pendingQueue.current = [];
    
    for (const item of queue) {
      try {
        await saveEntryToServer(item.key, item.item, item.entry);
      } catch (err) {
        // Re-queue if failed
        pendingQueue.current.push(item);
      }
    }
  }, [saveEntryToServer]);

  const fetchAreaItems = useCallback(async () => {
    if (!selectedArea) return;
    try {
      const response = await api.get(`/stock-items/area/${selectedArea}`);
      setAreaItems(response.data.items);
    } catch (err) {
      console.error('Failed to load items:', err);
    }
  }, [selectedArea]);

  const autoSaveEntry = useCallback((item, key, updatedEntry) => {
    // Clear existing timer for this item
    if (saveTimers.current[key]) {
      clearTimeout(saveTimers.current[key]);
    }

    // Set saving status
    setSaveStatus(prev => ({ ...prev, [key]: 'saving' }));
    
    // Cache immediately
    saveCachedEntries(key, updatedEntry);

    // Debounce save to server (1.5 seconds after last change)
    saveTimers.current[key] = setTimeout(async () => {
      if (!isOnline) {
        // Queue for later if offline
        pendingQueue.current.push({ key, item, entry: updatedEntry });
        setSaveStatus(prev => ({ ...prev, [key]: 'offline' }));
        return;
      }

      try {
        await saveEntryToServer(key, item, updatedEntry);
        setSaveStatus(prev => ({ ...prev, [key]: 'saved' }));
        clearCachedEntry(key);
        
        // Clear status after 2 seconds
        setTimeout(() => {
          setSaveStatus(prev => ({ ...prev, [key]: '' }));
        }, 2000);
      } catch (err) {
        console.error('Auto-save failed:', err);
        setSaveStatus(prev => ({ ...prev, [key]: 'error' }));
        // Keep in cache and queue for retry
        pendingQueue.current.push({ key, item, entry: updatedEntry });
      }
    }, 1500); // 1.5 second debounce
  }, [id, selectedArea, isOnline, saveCachedEntries, saveEntryToServer, clearCachedEntry]);

  const handleInputChange = (itemId, item, field, value) => {
    const key = `${selectedArea}-${itemId}`;
    const updatedEntry = {
      ...entries[key],
      [field]: value
    };
    
    setEntries(prev => ({
      ...prev,
      [key]: updatedEntry
    }));

    // Trigger auto-save
    autoSaveEntry(item, key, updatedEntry);
  };



  const handleCompleteStocktake = async () => {
    if (!window.confirm('Are you sure you want to complete this stocktake?')) {
      return;
    }

    try {
      await api.post(`/stocktakes/${id}/complete`);
      navigate(`/stocktake/${id}/report`);
    } catch (err) {
      alert('Failed to complete stocktake');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!stocktake) {
    return <div className="container">Stocktake not found</div>;
  }

  const currentAreaName = areas.find(a => a.id === selectedArea)?.name || '';

  const getSaveStatusIcon = (key) => {
    const status = saveStatus[key];
    if (status === 'saving') return ' 💾';
    if (status === 'saved') return ' ✓';
    if (status === 'offline') return ' 📴';
    if (status === 'error') return ' ⚠️';
    return '';
  };

  const getSaveStatusText = (key) => {
    const status = saveStatus[key];
    if (status === 'saving') return 'Saving...';
    if (status === 'saved') return 'Saved';
    if (status === 'offline') return 'Queued (offline)';
    if (status === 'error') return 'Save failed';
    return '';
  };

  return (
    <div className="container stock-room">
      <div className="card">
        <div className="card-header" style={{ background: 'var(--pink-light)', position: 'relative' }}>
          <div>
            <h2 className="card-title">{stocktake.name}</h2>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-light)' }}>
              {currentAreaName}
            </div>
          </div>
          {!isOnline && (
            <div style={{ 
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: '#ff9800',
              color: 'white',
              padding: '0.25rem 0.75rem',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: 600
            }}>
              📴 OFFLINE
            </div>
          )}
        </div>

        {areas.length === 0 ? (
          <div className="alert alert-info">
            No stock areas configured. Please set up stock areas first.
          </div>
        ) : (
          <>
            <div className="nav-tabs">
              {areas.map(area => (
                <button
                  key={area.id}
                  className={`nav-tab ${selectedArea === area.id ? 'active' : ''}`}
                  onClick={() => setSelectedArea(area.id)}
                >
                  {area.name}
                </button>
              ))}
            </div>

            {areaItems.length === 0 ? (
              <div className="alert alert-info">
                No items in this area. Please configure items for this area.
              </div>
            ) : (
              <div>
                {areaItems.map((item) => {
                  const key = `${selectedArea}-${item.id}`;
                  const entry = entries[key] || {};
                  
                  return (
                    <div key={item.id} id={`item-${item.id}`} className="stock-item">
                      <div className="stock-item-header">
                        <div style={{ flex: 1 }}>
                          <div className="stock-item-name">
                            {item.name}
                            {getSaveStatusIcon(key)}
                          </div>
                          {item.code && <div className="stock-item-code">#{item.code}</div>}
                        </div>
                        {saveStatus[key] && (
                          <div style={{ 
                            fontSize: '0.75rem',
                            color: saveStatus[key] === 'saved' ? 'var(--success)' : 
                                   saveStatus[key] === 'error' ? 'var(--error)' :
                                   saveStatus[key] === 'offline' ? '#ff9800' : 'var(--text-light)',
                            fontWeight: 600
                          }}>
                            {getSaveStatusText(key)}
                          </div>
                        )}
                      </div>

                      <div className="stock-inputs">
                        {item.has_cartons !== 0 && (
                          <div className="stock-input-group">
                            <label className="stock-input-label">
                              {item.carton_note || 'Cartons'}
                            </label>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              className="stock-input"
                              value={entry.cartons || ''}
                              onChange={(e) => handleInputChange(item.id, item, 'cartons', e.target.value)}
                              placeholder="0"
                            />
                            {item.units_per_carton && parseFloat(item.units_per_carton) > 0 && (
                              <div className="stock-input-note">
                                {item.units_per_carton} {item.unit_type} per carton
                              </div>
                            )}
                          </div>
                        )}

                        {item.has_sleeves !== 0 && (
                          <div className="stock-input-group">
                            <label className="stock-input-label">Sleeves</label>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              className="stock-input"
                              value={entry.sleeves || ''}
                              onChange={(e) => handleInputChange(item.id, item, 'sleeves', e.target.value)}
                              placeholder="0"
                            />
                            {item.units_per_sleeve && parseFloat(item.units_per_sleeve) > 0 && (
                              <div className="stock-input-note">
                                {item.units_per_sleeve} {item.unit_type} per sleeve
                              </div>
                            )}
                          </div>
                        )}

                        {item.has_units !== 0 && (
                          <div className="stock-input-group">
                            <label className="stock-input-label">
                              {item.unit_note || `Unit (${item.unit_type})`}
                            </label>
                            <input
                              inputMode="decimal"
                              type="number"
                              step="0.01"
                              className="stock-input"
                              value={entry.units || ''}
                              onChange={(e) => handleInputChange(item.id, item, 'units', e.target.value)}
                              placeholder="0"
                            />
                          </div>
                        )}
                      </div>

                      {entry.notes !== undefined && entry.notes !== '' && (
                        <div className="mt-2">
                          <textarea
                            className="form-textarea"
                            placeholder="Notes (optional)"
                            value={entry.notes || ''}
                            onChange={(e) => handleInputChange(item.id, 'notes', e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="bottom-nav">
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Save & Exit
        </button>
        <button className="btn btn-success" onClick={handleCompleteStocktake}>
          Complete Stocktake
        </button>
      </div>
    </div>
  );
};

export default Stocktake;
